import {
  ai_suggestion,
  application_log,
  category_mapping,
  feed_event,
  focus_session,
  isNull,
  pomodoro_config,
  project,
  project_member,
  reaction,
  sql,
  tag,
  task,
  time_entry,
  workspace,
  workspace_member,
} from "@time-tracker/powersync";
import type { AnySQLiteColumn, SQLiteTable } from "drizzle-orm/sqlite-core";

/**
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ SOFT-DELETE — CENTRALIZED LIVE LAYER (single choke point)              │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * This module is the ONLY place where the invariant “live row = `deleted_at
 * IS NULL`” is expressed for time-tracker tables with a trash system. Every
 * live read (routes under `apps/web/src/routes/_app/chronos/*`, the
 * `chronos-sync` feature) composes these predicates. The ONLY reads that see deleted
 * rows are the trash (`routes/_app/chronos/trash.tsx`, explicitly
 * `isNotNull(deleted_at)`) and harmless id-scoped lookups, all declared in
 * the guard's whitelist (`guard-manifest.ts`).
 *
 * Why centralize? The scattered invariant already caused real bugs in this
 * campaign (a time-tracker PATCH that ignored `deleted_at` for `task`/`tag`/
 * `application_log` — see Rule A of the server kit) and in other clones (⌘K for
 * time entry, checkUnsyncedActor). With a single choke point + anti-
 * regression guard, adding a raw read of these tables outside the whitelist
 * BREAKS the build (see `live/__tests__/live-access-guard.test.ts`).
 *
 * ─── Triage of which table this layer protects (SOFT_DELETE_TABLES) ─────────
 * `user.deleted_at` is the ACCOUNT deletion grace period (auth
 * domain, `use_cases/auth::purge_expired_account_deletions`), NOT a domain
 * trash system: no Chronos view treats users as "recoverable
 * items" — it is EXCLUDED from this layer (`user` reads in
 * `theme/queries.ts`/`users/queries.ts`/`team.tsx` are id-scoped lookups or
 * joins for a specific member, not listings that need to hide accounts during
 * the grace period). `ai_suggestion` still has NO client reader (an AI-suggestions
 * feature with no web UI) — its filter is defined for completeness but
 * it does not appear in the guard's whitelist (there are no hits to classify).
 *
 * ─── Cohort hierarchy (verified against `packages/powersync/src/schema.ts`) ──
 * A CHILD row is live only if its own `deleted_at IS NULL` AND its
 * trash-enabled DIRECT parent is not in the trash (`NOT EXISTS` cohort, one
 * level only — the grandparent is not followed):
 *   - project          → workspace       (workspace_id)
 *   - task             → project         (project_id)
 *   - time_entry       → project         (project_id) + task (task_id, optional)
 *   - tag              → workspace       (workspace_id)
 *   - project_member   → project         (project_id)
 *   - workspace_member → workspace       (workspace_id)
 *   - feed_event       → workspace       (workspace_id)
 *   - reaction         → feed_event      (feed_event_id)
 *   - focus_session    → project (optional) + task (optional)
 *   - category_mapping → project         (project_id)
 * `time_entry`/`focus_session` reference `task_id` OPTIONALLY (a
 * block may have no assigned task): the `task` cohort is safe with
 * `task_id IS NULL` because `NOT EXISTS` finds no row with `id = NULL`
 * and therefore excludes nothing (vacuously true).
 *
 * Roots (only their own `deleted_at`, with no trash-enabled parent):
 *   - workspace (tree root)
 *   - pomodoro_config (1:1 config per user; `user_id` is not a domain
 *     trash-enabled parent — see the `user` triage above)
 *   - application_log (`assigned_time_entry_id` is an OPTIONAL assignment
 *     link, not an ownership relation: an application log still
 *     exists if the assigned block is deleted — it is NOT a cohort)
 *   - ai_suggestion (same reasoning as application_log:
 *     `assigned_time_entry_id` is an assignment link, not ownership)
 */

/**
 * Generic cohort predicate: the child row hangs from a parent that is NOT
 * in the trash. With parent soft-delete, children persist intact
 * (so the entire cohort can be restored atomically) but disappear from
 * live views. If `childFk` is NULL (optional relationship), the subquery finds
 * no row and the predicate is true (it excludes nothing).
 */
function parentNotTrashed(
  parent: SQLiteTable & { id: AnySQLiteColumn; deleted_at: AnySQLiteColumn },
  childFk: AnySQLiteColumn,
) {
  return sql`NOT EXISTS (SELECT 1 FROM ${parent} WHERE ${parent.id} = ${childFk} AND ${parent.deleted_at} IS NOT NULL)`;
}

// ─── Roots (only their own deleted_at) ───────────────────────────────────────

export function liveWorkspaceFilters() {
  return [isNull(workspace.deleted_at)];
}

export function livePomodoroConfigFilters() {
  return [isNull(pomodoro_config.deleted_at)];
}

export function liveApplicationLogFilters() {
  return [isNull(application_log.deleted_at)];
}

export function liveAiSuggestionFilters() {
  return [isNull(ai_suggestion.deleted_at)];
}

// ─── Children with cohorts (trash-enabled parent not deleted) ────────────────

export function liveProjectFilters() {
  return [isNull(project.deleted_at), parentNotTrashed(workspace, project.workspace_id)];
}

export function liveTaskFilters() {
  return [isNull(task.deleted_at), parentNotTrashed(project, task.project_id)];
}

export function liveTimeEntryFilters() {
  return [
    isNull(time_entry.deleted_at),
    parentNotTrashed(project, time_entry.project_id),
    parentNotTrashed(task, time_entry.task_id),
  ];
}

export function liveTagFilters() {
  return [isNull(tag.deleted_at), parentNotTrashed(workspace, tag.workspace_id)];
}

export function liveProjectMemberFilters() {
  return [isNull(project_member.deleted_at), parentNotTrashed(project, project_member.project_id)];
}

export function liveWorkspaceMemberFilters() {
  return [
    isNull(workspace_member.deleted_at),
    parentNotTrashed(workspace, workspace_member.workspace_id),
  ];
}

export function liveFeedEventFilters() {
  return [isNull(feed_event.deleted_at), parentNotTrashed(workspace, feed_event.workspace_id)];
}

export function liveReactionFilters() {
  return [isNull(reaction.deleted_at), parentNotTrashed(feed_event, reaction.feed_event_id)];
}

export function liveFocusSessionFilters() {
  return [
    isNull(focus_session.deleted_at),
    parentNotTrashed(project, focus_session.project_id),
    parentNotTrashed(task, focus_session.task_id),
  ];
}

export function liveCategoryMappingFilters() {
  return [
    isNull(category_mapping.deleted_at),
    parentNotTrashed(project, category_mapping.project_id),
  ];
}
